import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Collapse, Input, Select } from "antd";
import React, { useMemo } from "react";
import { AddButton } from "~/components/buttons/AddButton.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { $t } from "~/i18n.js";

export const ChecklistEditor = ({
  checklistStart,
  setChecklistStart,
  checklistEnd,
  setChecklistEnd,
}) => {
  return (
    <div>
      <HelpLine style={{marginBottom: 12}}>
        {$t(
          "Checklists kunnen worden gebruikt aan zowel het begin als einde van de dag. Ze mogen worden uitgevoerd door groepen die de permissie 'inspectie uitvoeren' hebben.",
        )}
      </HelpLine>

      <p className="subtitle">{$t("Start checklist")}</p>
      <ChecklistTemplateEditor
        value={checklistStart}
        onChange={setChecklistStart}
      />

      <p className="subtitle" style={{ marginTop: 16 }}>
        {$t("Einde checklist")}
      </p>
      <ChecklistTemplateEditor
        value={checklistEnd}
        onChange={setChecklistEnd}
      />
    </div>
  );
};

const ChecklistTemplateEditor = ({ value = [], onChange }) => {
  const types = useMemo(
    () => [
      { value: "checkbox", label: $t("Vinkje") },
      { value: "text", label: $t("Tekst") },
      { value: "number", label: $t("Nummer") },
      { value: "date", label: $t("Datum") },
      { value: "time", label: $t("Tijd") },
      { value: "multi", label: $t("Meerdere opties") },
      { value: "radio", label: $t("Enkele optie") },
    ],
    [],
  );
  const handleCellChange = (rowIndex, newData) => {
    const newRows = value.map((row, idx) =>
      idx === rowIndex ? { ...row, ...newData } : row,
    );
    onChange(newRows);
  };

  const handleReorder = (fromIdx, toIdx) => {
    const newRows = [...value];
    const [moved] = newRows.splice(fromIdx, 1);
    newRows.splice(toIdx, 0, moved);
    onChange(newRows);
  };

  return (
    <div>
      {value && value.length > 0 && (
        <Collapse
          size="small"
          items={value.map((row, idx) => ({
            key: idx,
            label: row.name || $t("Nieuwe check"),
            extra: (
              <div
                className="row"
                style={{ gap: 4 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  shape="circle"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0}
                  onClick={() => handleReorder(idx, idx - 1)}
                />
                <Button
                  size="small"
                  shape="circle"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === value.length - 1}
                  onClick={() => handleReorder(idx, idx + 1)}
                />
                <Button
                  size="small"
                  danger
                  shape="circle"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    const newRows = value.filter((_, i) => i !== idx);
                    onChange(newRows);
                  }}
                />
              </div>
            ),
            children: (
              <>
                <div className="row" style={{ marginBottom: 8, gap: 8 }}>
                  <Input
                    style={{ width: 150 }}
                    value={row.field}
                    variant={row.field ? "filled" : "outlined"}
                    status={!row.field ? "error" : ""}
                    onChange={(e) => {
                      const cleanKey = e.target.value.replace(
                        /[^a-zA-Z0-9_]/g,
                        "",
                      );
                      handleCellChange(idx, { field: cleanKey });
                    }}
                    size="small"
                    placeholder={$t("Veldnaam (key)")}
                  />
                  <Input
                    value={row.name}
                    onChange={(e) =>
                      handleCellChange(idx, { name: e.target.value })
                    }
                    size="small"
                    placeholder={$t("Omschrijving")}
                  />
                </div>

                <Input.TextArea
                  value={row.extra}
                  onChange={(e) =>
                    handleCellChange(idx, { extra: e.target.value })
                  }
                  size="small"
                  rows={2}
                  placeholder={$t("Toelichting (optioneel)")}
                  style={{ marginBottom: 8 }}
                />

                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <Select
                    value={row.input_type}
                    onChange={(v) => handleCellChange(idx, { input_type: v })}
                    size="small"
                    style={{ width: 160 }}
                    options={types}
                    placeholder={$t("Type")}
                  />

                  <Checkbox
                    checked={!!row.required}
                    onChange={(e) =>
                      handleCellChange(idx, { required: e.target.checked })
                    }
                  >
                    {$t("Verplicht")}
                  </Checkbox>

                  {(row.input_type === "multi" ||
                    row.input_type === "radio") && (
                    <Select
                      mode="tags"
                      tokenSeparators={[","]}
                      size="small"
                      style={{ minWidth: 160 }}
                      value={row.options}
                      onChange={(e) => handleCellChange(idx, { options: e })}
                      placeholder={$t("Opties")}
                    />
                  )}
                </div>
              </>
            ),
          }))}
        />
      )}

      <div style={{ marginTop: 6, marginLeft: 6 }}>
        <AddButton
          onClick={() => {
            onChange([
              ...(value || []),
              {
                field: "",
                name: "",
                extra: "",
                input_type: "checkbox",
                options: [],
                required: true,
              },
            ]);
          }}
        />
      </div>
    </div>
  );
};
